import I18n from 'I18n';
import { useMemo } from 'react';
import { useGetRateCardQuery, useGetUsagePeriodUsageQuery } from '../../__generated__/chirp/com/hubspot/usagebasedbilling/experience/rpc/UsageBasedBillingExperienceRpc.hooks';
import { useGetGovernanceStatusQuery } from '../../__generated__/chirp/com/hubspot/usagebasedbilling/experience/rpc/UsageBasedBillingGovernanceRpc.hooks';
// Known enum values for runtime type checking
const KNOWN_BILLABLE_METRIC_TYPES = ['BREEZE_CONTACT_ENRICHMENT', 'BREEZE_COMPANY_ENRICHMENT', 'BREEZE_SMART_PROPERTY', 'CUSTOMER_AGENT_TEXT_CONVERSATION', 'PROSPECTING_AGENT_CONTACT_MONITORING', 'PROSPECTING_AGENT_COMPANY_RESEARCH', 'DATA_AGENT_PROMPT_RESPONSE', 'WORKFLOWS_BREEZE_ACTION', 'INTENT_COMPANY_MONITORING', 'DATA_HUB_SMALL_SYNC', 'DATA_HUB_MEDIUM_SYNC', 'DATA_HUB_LARGE_SYNC'];
const VERIFIED_KNOWN_BILLABLE_METRIC_TYPES = KNOWN_BILLABLE_METRIC_TYPES;
const KNOWN_FEATURE_TYPES = ['CUSTOMER_AGENT', 'ENRICHMENT', 'PROSPECTING_AGENT', 'DATA_AGENT', 'WORKFLOWS', 'INTENT', 'DATA_STUDIO_SYNC'];
const VERIFIED_KNOWN_FEATURE_TYPES = KNOWN_FEATURE_TYPES;
export function isKnownBillableMetricType(value) {
  return VERIFIED_KNOWN_BILLABLE_METRIC_TYPES.includes(value);
}
export function isKnownFeatureType(value) {
  return VERIFIED_KNOWN_FEATURE_TYPES.includes(value);
}
/**
 * Hook to determine if a credit-based feature is disabled based on governance status
 *
 * For KnownBillableMetricType: checks the governance status directly
 * For KnownFeatureType: checks if all billable metrics for that feature type are enabled
 *
 * @param creditFeature - The billable metric type or feature type to check status of
 * @returns Object containing isDisabled boolean and related data
 */
export function useIsCreditFeatureDisabled(creditFeature) {
  // Runtime type checking
  const isBillableMetricType = isKnownBillableMetricType(creditFeature);
  const isFeatureType = isKnownFeatureType(creditFeature);
  const {
    data: governanceData,
    loading: governanceLoading,
    error: governanceError
  } = useGetGovernanceStatusQuery();
  const {
    data: rateCardData,
    loading: rateCardLoading,
    error: rateCardError
  } = useGetRateCardQuery({
    skip: !isFeatureType
  });
  const {
    data: usagePeriodData,
    loading: usagePeriodLoading,
    error: usagePeriodError
  } = useGetUsagePeriodUsageQuery({
    variables: {
      showBreakdown: false
    }
  });
  return useMemo(() => {
    // Combine loading states
    const loading = governanceLoading || rateCardLoading || usagePeriodLoading;

    // Combine errors - return the first error encountered
    const error = governanceError || rateCardError || usagePeriodError;

    // Extract reset date from usage period data - add one day to endDate
    const resetDate = usagePeriodData !== null && usagePeriodData !== void 0 && usagePeriodData.endDate ? I18n.moment(usagePeriodData.endDate).add(1, 'day').format('YYYY-MM-DD') : undefined;

    // Default to disabled if loading or error
    if (loading || error || !governanceData) {
      return {
        isDisabled: true,
        loading,
        error,
        resetDate
      };
    }
    let isDisabled;
    if (isBillableMetricType) {
      // For KnownBillableMetricType: direct lookup in governance data
      const featureState = governanceData.featureStates[creditFeature];
      isDisabled = !(featureState !== null && featureState !== void 0 && featureState.isEnabled);
    } else if (isFeatureType && rateCardData) {
      // For KnownFeatureType: check if all billable metrics for this feature type are enabled
      const billableMetricsForFeatureType = rateCardData.rates.filter(rate => rate.featureType === creditFeature).map(rate => rate.billableMetricType);
      if (billableMetricsForFeatureType.length === 0) {
        // No billable metrics found for this feature type, consider it disabled
        isDisabled = true;
      } else {
        // Check if any billable metrics for this feature type are enabled
        isDisabled = !billableMetricsForFeatureType.some(billableMetricType => {
          const featureState = governanceData.featureStates[billableMetricType];
          return featureState === null || featureState === void 0 ? void 0 : featureState.isEnabled;
        });
      }
    } else {
      // Unknown type or missing rate card data, default to disabled
      isDisabled = true;
    }
    return {
      isDisabled,
      loading,
      error,
      resetDate
    };
  }, [governanceData, rateCardData, usagePeriodData, creditFeature, governanceLoading, governanceError, rateCardLoading, rateCardError, usagePeriodLoading, usagePeriodError, isBillableMetricType, isFeatureType]);
}